import { useState, useEffect } from "react";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

  :root {
    --bg: #0A0E1A;
    --surface: #111827;
    --elevated: #1C2333;
    --border: #1F2D45;
    --accent: #00E5A0;
    --accent-dim: #0D2E22;
    --text: #EEF2FF;
    --muted: #8B9EC3;
    --faint: #4A5878;
    --negative: #FF5C7A;
    --warning: #FFB547;
  }

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }

  .pp-body {
    background: var(--bg);
    color: var(--text);
    font-family: 'DM Sans', sans-serif;
    font-size: 15px;
    line-height: 1.75;
    min-height: 100vh;
    overflow-x: hidden;
    position: relative;
  }

  .pp-grid-bg {
    position: fixed;
    inset: 0;
    background-image:
      linear-gradient(var(--border) 1px, transparent 1px),
      linear-gradient(90deg, var(--border) 1px, transparent 1px);
    background-size: 48px 48px;
    opacity: 0.35;
    pointer-events: none;
    z-index: 0;
  }

  .pp-glow {
    position: fixed;
    top: -120px;
    left: 50%;
    transform: translateX(-50%);
    width: 700px;
    height: 500px;
    background: radial-gradient(ellipse at center, rgba(0,229,160,.13) 0%, transparent 70%);
    pointer-events: none;
    z-index: 0;
    animation: glow-pulse 4s ease-in-out infinite alternate;
  }

  @keyframes glow-pulse {
    from { opacity: .7; transform: translateX(-50%) scale(1); }
    to   { opacity: 1;  transform: translateX(-50%) scale(1.08); }
  }

  .pp-wrap {
    position: relative;
    z-index: 1;
    max-width: 780px;
    margin: 0 auto;
    padding: 0 24px 80px;
  }

  .pp-hero {
    padding: 64px 0 48px;
    text-align: center;
    animation: fade-up .7s ease both;
  }

  .pp-hero-badge {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: var(--accent-dim);
    border: 1px solid rgba(0,229,160,.3);
    border-radius: 100px;
    padding: 5px 14px;
    font-family: 'Syne', sans-serif;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1.4px;
    color: var(--accent);
    text-transform: uppercase;
    margin-bottom: 22px;
  }

  .pp-hero h1 {
    font-family: 'Syne', sans-serif;
    font-size: clamp(32px, 6vw, 52px);
    font-weight: 800;
    letter-spacing: -1.5px;
    line-height: 1.08;
    margin-bottom: 16px;
  }

  .pp-hero h1 span { color: var(--accent); }

  .pp-hero-meta {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 10px 20px;
    color: var(--faint);
    font-size: 13px;
    margin-top: 18px;
  }

  .pp-hero-meta span {
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .pp-divider {
    height: 1px;
    background: linear-gradient(90deg, transparent, var(--border), transparent);
    margin: 40px 0;
  }

  .pp-scope-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin: 20px 0 32px;
  }

  @media (max-width: 540px) { .pp-scope-grid { grid-template-columns: 1fr; } }

  .pp-scope-pill {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 14px 16px;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    transition: border-color .2s, transform .2s;
  }

  .pp-scope-pill:hover {
    border-color: rgba(0,229,160,.35);
    transform: translateY(-2px);
  }

  .pp-scope-icon {
    width: 34px; height: 34px;
    min-width: 34px;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .pp-scope-pill p {
    font-size: 12px;
    color: var(--muted);
    line-height: 1.5;
    margin-top: 2px;
  }

  .pp-scope-pill strong {
    font-size: 13px;
    color: var(--text);
    display: block;
    margin-bottom: 2px;
  }

  .pp-section {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 18px;
    padding: 28px 28px 24px;
    margin-bottom: 16px;
    animation: fade-up .5s ease both;
    transition: border-color .25s;
  }

  .pp-section:hover { border-color: rgba(0,229,160,.18); }

  .pp-section-head {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
  }

  .pp-section-icon {
    width: 38px; height: 38px;
    min-width: 38px;
    border-radius: 11px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .pp-section h2 {
    font-family: 'Syne', sans-serif;
    font-size: 16px;
    font-weight: 700;
    color: var(--text);
    letter-spacing: -.2px;
  }

  .pp-section p, .pp-section li {
    color: var(--muted);
    font-size: 14px;
    line-height: 1.75;
  }

  .pp-section ul {
    padding-left: 18px;
    margin-top: 8px;
  }

  .pp-section li { margin-bottom: 6px; }

  .pp-section a {
    color: var(--accent);
    text-decoration: none;
    border-bottom: 1px solid rgba(0,229,160,.3);
    transition: border-color .15s;
  }

  .pp-section a:hover { border-color: var(--accent); }

  .pp-section p + p { margin-top: 10px; }

  .pp-highlight {
    background: var(--accent-dim);
    border: 1px solid rgba(0,229,160,.2);
    border-radius: 10px;
    padding: 12px 16px;
    margin-top: 14px;
    font-size: 13px;
    color: rgba(0,229,160,.85);
    display: flex;
    gap: 10px;
    align-items: flex-start;
  }

  .pp-warn-box {
    background: rgba(255,92,122,.06);
    border: 1px solid rgba(255,92,122,.2);
    border-radius: 10px;
    padding: 12px 16px;
    margin-top: 14px;
    font-size: 13px;
    color: rgba(255,92,122,.85);
    display: flex;
    gap: 10px;
    align-items: flex-start;
  }

  .pp-contact-card {
    background: var(--accent-dim);
    border: 1px solid rgba(0,229,160,.25);
    border-radius: 18px;
    padding: 32px;
    text-align: center;
    margin-top: 40px;
  }

  .pp-contact-card h3 {
    font-family: 'Syne', sans-serif;
    font-size: 20px;
    font-weight: 700;
    color: var(--text);
    margin-bottom: 8px;
  }

  .pp-contact-card p { color: var(--muted); font-size: 14px; margin-bottom: 20px; }

  .pp-contact-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: var(--accent);
    color: var(--bg);
    font-family: 'Syne', sans-serif;
    font-weight: 700;
    font-size: 14px;
    padding: 12px 24px;
    border-radius: 100px;
    text-decoration: none;
    transition: opacity .2s, transform .2s;
  }

  .pp-contact-btn:hover { opacity: .88; transform: translateY(-1px); }

  .pp-footer {
    text-align: center;
    padding: 32px 0 0;
    color: var(--faint);
    font-size: 12px;
  }

  .pp-footer strong { color: var(--muted); }

  @keyframes fade-up {
    from { opacity: 0; transform: translateY(18px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

// ── SVG Icons ──────────────────────────────────────────────────────────────

const IconInfo = ({ size = 18, fill = "var(--accent)" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" style={{ fill }}>
    <path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
  </svg>
);

const IconLock = ({ size = 18, fill = "#378ADD" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" style={{ fill }}>
    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zM9 8V6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9z" />
  </svg>
);

const IconCheck = ({ size = 18, fill = "var(--accent)" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" style={{ fill }}>
    <path d="M9 16.2l-3.5-3.5-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z" />
  </svg>
);

const IconFile = ({ size = 18, fill = "var(--accent)" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" style={{ fill }}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm4 18H6V4h7v5h5v11z" />
  </svg>
);

const IconPeople = ({ size = 18, fill = "var(--accent)" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" style={{ fill }}>
    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
  </svg>
);

const IconShield = ({ size = 18, fill = "var(--accent)" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" style={{ fill }}>
    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />
  </svg>
);

const IconWarning = ({ size = 18, fill = "var(--negative)" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" style={{ fill }}>
    <path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
  </svg>
);

const IconShapes = ({ size = 18, fill = "var(--warning)" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" style={{ fill }}>
    <path d="M12 2l-5.5 9h11L12 2zm0 3.84L14.6 10h-5.2L12 5.84zM17.5 13c-2.49 0-4.5 2.01-4.5 4.5s2.01 4.5 4.5 4.5 4.5-2.01 4.5-4.5-2.01-4.5-4.5-4.5zm0 7c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5zM3 21.5h8v-8H3v8zm2-6h4v4H5v-4z" />
  </svg>
);

const IconHistory = ({ size = 18, fill = "#378ADD" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" style={{ fill }}>
    <path d="M13 3a9 9 0 1 0 9 9h-2a7 7 0 1 1-7-7v3l4-4-4-4v3z" />
  </svg>
);

const IconClock = ({ size = 18, fill = "var(--warning)" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" style={{ fill }}>
    <path d="M12 2v10l4.24 4.24-1.42 1.42L10 12.01V2h2zm-1 18a8.001 8.001 0 0 1-7.93-7H1l3.5-3.5L8 13H5.08A7.003 7.003 0 0 0 12 20a7 7 0 0 0 6.92-6h2.01A9.003 9.003 0 0 1 12 20z" />
  </svg>
);

const IconChild = ({ size = 18, fill = "var(--negative)" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" style={{ fill }}>
    <path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
  </svg>
);

const IconPurple = ({ size = 18, fill = "#7F77DD" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" style={{ fill }}>
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
  </svg>
);

const IconEmail = ({ size = 15, fill = "var(--bg)" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" style={{ fill }}>
    <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
  </svg>
);

// ── Sub-components ─────────────────────────────────────────────────────────

function SectionIcon({ bg, children }) {
  return (
    <div className="pp-section-icon" style={{ background: bg }}>
      {children}
    </div>
  );
}

function Section({ iconBg, icon, title, children, delay = 0 }) {
  return (
    <div className="pp-section" style={{ animationDelay: `${delay}s` }}>
      <div className="pp-section-head">
        <SectionIcon bg={iconBg}>{icon}</SectionIcon>
        <h2>{title}</h2>
      </div>
      {children}
    </div>
  );
}

function Highlight({ children }) {
  return (
    <div className="pp-highlight">
      <IconInfo size={16} />
      <span>{children}</span>
    </div>
  );
}

function WarnBox({ children }) {
  return (
    <div className="pp-warn-box">
      <IconWarning size={16} />
      <span>{children}</span>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────

export default function PrivacyPolicy() {
  useEffect(() => {
    const styleEl = document.createElement("style");
    styleEl.textContent = styles;
    document.head.appendChild(styleEl);
    return () => document.head.removeChild(styleEl);
  }, []);

  return (
    <div className="pp-body">
      <div className="pp-grid-bg" />
      <div className="pp-glow" />

      <div className="pp-wrap">

        {/* ── Hero ── */}
        <header className="pp-hero">
          <div className="pp-hero-badge">
            <IconInfo size={12} />
            Legal Document
          </div>
          <h1>Privacy <span>Policy</span></h1>
          <p style={{ color: "var(--muted)", fontSize: 15, maxWidth: 480, margin: "0 auto" }}>
            How <strong style={{ color: "var(--text)" }}>EarningsPulse</strong> collects, uses, and protects your data.
          </p>
          <div className="pp-hero-meta">
            <span>
              <svg width={13} height={13} viewBox="0 0 24 24" style={{ fill: "var(--faint)" }}>
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z" />
              </svg>
              Developer: AlphaY
            </span>
            <span>
              <svg width={13} height={13} viewBox="0 0 24 24" style={{ fill: "var(--faint)" }}>
                <path d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zm-7 14l-5-5 1.41-1.41L12 14.17l7.59-7.59L21 8l-9 9z" />
              </svg>
              Effective: April 2, 2026
            </span>
            <span>
              <IconInfo size={13} fill="var(--faint)" />
              Free App · Ad Supported
            </span>
          </div>
        </header>

        <div className="pp-divider" />

        {/* ── Overview ── */}
        <Section
          iconBg="rgba(0,229,160,.1)"
          icon={<IconFile />}
          title="Overview"
          delay={0.05}
        >
          <p>
            <strong style={{ color: "var(--text)" }}>AlphaY</strong> ("developer", "I", "we") built{" "}
            <strong style={{ color: "var(--text)" }}>EarningsPulse</strong> as a free, ad-supported application.
            This service is provided at no cost and is intended for use as-is.
          </p>
          <p>
            This page informs you of our policies regarding the collection, use, and disclosure of
            personal information when you use our app. By using EarningsPulse, you agree to the
            collection and use of information in accordance with this policy.
          </p>
          <Highlight>
            We never sell your personal data to third parties. EarningsPulse only reads your AdMob data — it cannot modify, delete, or manage your account in any way.
          </Highlight>
        </Section>

        {/* ── OAuth Scopes ── */}
        <Section
          iconBg="rgba(55,138,221,.12)"
          icon={<IconLock />}
          title="OAuth 2.0 Scopes We Request"
          delay={0.10}
        >
          <p>EarningsPulse requests the following Google API permissions to function:</p>
          <div className="pp-scope-grid">
            <div className="pp-scope-pill">
              <div className="pp-scope-icon" style={{ background: "rgba(0,229,160,.1)" }}>
                <IconCheck size={16} />
              </div>
              <div>
                <strong>admob.readonly</strong>
                <p>Read-only access to view your AdMob earnings, impressions, and ad performance data.</p>
              </div>
            </div>
            <div className="pp-scope-pill">
              <div className="pp-scope-icon" style={{ background: "rgba(55,138,221,.1)" }}>
                <IconCheck size={16} fill="#378ADD" />
              </div>
              <div>
                <strong>adsense.readonly</strong>
                <p>Read-only access to view your AdSense performance and revenue data.</p>
              </div>
            </div>
          </div>
          <WarnBox>
            These are <strong>read-only</strong> scopes. EarningsPulse can never make changes to your AdMob or AdSense account settings, ads, or billing.
          </WarnBox>
        </Section>

        {/* ── Information Collected ── */}
        <Section
          iconBg="rgba(127,119,221,.12)"
          icon={<IconPurple />}
          title="Information We Collect"
          delay={0.15}
        >
          <p><strong style={{ color: "var(--text)" }}>User-provided information</strong></p>
          <p>
            When you sign in with Google, we access your basic profile (name, email, profile photo)
            via Firebase Authentication. We do not store your Google credentials — authentication
            is handled entirely by Google and Firebase.
          </p>
          <p style={{ marginTop: 14 }}><strong style={{ color: "var(--text)" }}>Automatically collected information</strong></p>
          <p>The app may automatically collect non-personal information including:</p>
          <ul>
            <li>Device type and model</li>
            <li>Operating system and version</li>
            <li>Mobile browser type</li>
            <li>IP address (used for region detection only)</li>
            <li>App usage patterns and session duration</li>
            <li>Crash and error logs (non-personal, used to improve the app)</li>
          </ul>
          <p style={{ marginTop: 14 }}><strong style={{ color: "var(--text)" }}>AdMob & AdSense data</strong></p>
          <p>
            Your AdMob and AdSense earnings data is fetched directly from Google's API and displayed
            in the app. This data is <strong style={{ color: "var(--text)" }}>never stored on our servers</strong> —
            it is only held temporarily in memory while you use the app.
          </p>
        </Section>

        {/* ── How We Use It ── */}
        <Section
          iconBg="rgba(255,181,71,.1)"
          icon={<IconShapes />}
          title="How We Use Your Information"
          delay={0.20}
        >
          <p>The information we collect is used solely to:</p>
          <ul>
            <li>Authenticate you securely via Google Sign-In</li>
            <li>Display your AdMob and AdSense earnings dashboard</li>
            <li>Improve app performance and fix bugs using crash reports</li>
            <li>Serve relevant ads within the app (non-personal, anonymised)</li>
          </ul>
          <Highlight>
            We do not use your AdMob or AdSense data for advertising profiling, and we do not pass your earnings data to any third party.
          </Highlight>
        </Section>

        {/* ── Third Parties ── */}
        <Section
          iconBg="rgba(0,229,160,.1)"
          icon={<IconPeople />}
          title="Third Party Services"
          delay={0.25}
        >
          <p>
            EarningsPulse uses the following third-party services. Each has its own privacy policy
            governing how they handle your data:
          </p>
          <ul>
            <li><a href="https://firebase.google.com/support/privacy" target="_blank" rel="noreferrer">Google Firebase</a> — Authentication &amp; Cloud Functions</li>
            <li><a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Google Sign-In</a> — Secure login</li>
            <li><a href="https://support.google.com/admob/answer/6128543" target="_blank" rel="noreferrer">Google AdMob</a> — In-app advertising</li>
            <li><a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Google AdMob API</a> — Fetching your earnings data</li>
          </ul>
          <p style={{ marginTop: 12 }}>
            We may disclose information only when required by law, to protect our legal rights, to
            protect user safety, or to trusted service providers who operate under confidentiality
            obligations consistent with this policy.
          </p>
        </Section>

        {/* ── Data Retention ── */}
        <Section
          iconBg="rgba(55,138,221,.12)"
          icon={<IconHistory />}
          title="Data Retention"
          delay={0.30}
        >
          <p>
            We retain user-provided data (your sign-in session) for as long as you use the app.
            Your AdMob earnings data is <strong style={{ color: "var(--text)" }}>never persisted</strong> to
            any server — it lives in-memory only for your active session.
          </p>
          <p>
            If you uninstall the app, all locally stored data is automatically deleted.
            If you wish to request deletion of any data associated with your account, contact us at{" "}
            <a href="mailto:alphay28071994@gmail.com">alphay28071994@gmail.com</a>.
          </p>
        </Section>

        {/* ── Children ── */}
        <Section
          iconBg="rgba(255,92,122,.1)"
          icon={<IconChild />}
          title="Children's Privacy"
          delay={0.35}
        >
          <p>
            EarningsPulse is not directed to anyone under the age of <strong style={{ color: "var(--text)" }}>16</strong>.
            We do not knowingly collect personally identifiable information from children under 16.
          </p>
          <p>
            If you are a parent or guardian and believe your child has provided personal information
            to us, please contact us immediately at{" "}
            <a href="mailto:alphay28071994@gmail.com">alphay28071994@gmail.com</a>{" "}
            and we will take prompt action to delete that information.
          </p>
        </Section>

        {/* ── Security ── */}
        <Section
          iconBg="rgba(0,229,160,.1)"
          icon={<IconShield />}
          title="Security"
          delay={0.40}
        >
          <p>
            We are committed to protecting your information. EarningsPulse uses industry-standard
            security practices including:
          </p>
          <ul>
            <li>OAuth 2.0 for secure Google authentication</li>
            <li>Firebase Authentication with encrypted token storage</li>
            <li>HTTPS / TLS for all API communication</li>
            <li>Read-only API scopes — no write access to your accounts</li>
          </ul>
          <p>
            While we strive to use commercially acceptable means to protect your data, no method of
            transmission over the internet is 100% secure. We cannot guarantee absolute security.
          </p>
        </Section>

        {/* ── Changes ── */}
        <Section
          iconBg="rgba(255,181,71,.1)"
          icon={<IconClock />}
          title="Changes to This Policy"
          delay={0.45}
        >
          <p>
            This Privacy Policy may be updated from time to time. We will notify you of any changes
            by posting the new policy on this page and updating the effective date above.
          </p>
          <p>
            We encourage you to review this Privacy Policy periodically. Continued use of the app
            after changes constitutes your acceptance of the updated policy.
          </p>
        </Section>

        {/* ── Consent ── */}
        <Section
          iconBg="rgba(0,229,160,.1)"
          icon={<IconCheck />}
          title="Your Consent"
          delay={0.50}
        >
          <p>
            By using EarningsPulse, you consent to this Privacy Policy. "Processing" means any
            operation performed on your data including collecting, storing, using, and disclosing
            information as described above and as necessary for the proper functioning of the app.
          </p>
        </Section>

        {/* ── Contact ── */}
        <div className="pp-contact-card">
          <h3>Questions or Concerns?</h3>
          <p>
            If you have any questions about this Privacy Policy or how your data is handled,
            don't hesitate to reach out.
          </p>
          <a href="mailto:alphay28071994@gmail.com" className="pp-contact-btn">
            <IconEmail />
            alphay28071994@gmail.com
          </a>
        </div>

        {/* ── Footer ── */}
        <footer className="pp-footer">
          <div className="pp-divider" style={{ margin: "32px 0 20px" }} />
          <p>© 2026 <strong>AlphaY</strong> · EarningsPulse · All rights reserved.</p>
          <p style={{ marginTop: 6 }}>This app is not affiliated with or endorsed by Google LLC.</p>
        </footer>

      </div>
    </div>
  );
}